import { useState, type CSSProperties, type ReactNode } from "react";

export interface BBox {
  /** percentages 0..100 */
  top: number;
  left: number;
  width: number;
  height: number;
  label?: string;
}

interface FrameProps {
  /** real image url; when absent a faux viewfinder subject is drawn */
  src?: string | null;
  alt?: string;
  /** paper variant for the light console */
  paper?: boolean;
  /** timecode cap shown top-right */
  cap?: string;
  /** bbox annotation overlay */
  bbox?: BBox | null;
  /** draw the faux subject blob (only when no src) */
  subject?: boolean;
  /** load the image eagerly (e.g. the first in-view still); otherwise lazy + async-decoded */
  eager?: boolean;
  /** reports the loaded image's natural aspect ratio (w/h) so callers can size by orientation */
  onRatio?: (ratio: number) => void;
  style?: CSSProperties;
  className?: string;
  children?: ReactNode;
}

/**
 * The signature placeholder "viewfinder still". Renders a real <img> when `src` is given,
 * otherwise the CSS-only faux subject from the design system. Optionally overlays a bbox.
 */
export function Frame({
  src,
  alt = "",
  paper,
  cap,
  bbox,
  subject = true,
  eager,
  onRatio,
  style,
  className,
  children,
}: FrameProps) {
  const cls = ["frame", paper && "frame--paper", className].filter(Boolean).join(" ");
  // Adopt the source image's natural aspect ratio so a landscape still isn't cropped into the
  // default 3:4 portrait box. This keeps the whole frame visible AND lets the bbox (% of the box)
  // and the highlighter overlay (which fills the box) line up exactly with the pixels instead of
  // drifting off the cropped-away region. Overrides any aspectRatio passed in `style`.
  const [ratio, setRatio] = useState<number | null>(null);
  return (
    <div className={cls} style={{ ...style, ...(ratio ? { aspectRatio: String(ratio) } : null) }}>
      {src ? (
        <img
          src={src}
          alt={alt}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          onLoad={(e) => {
            const im = e.currentTarget;
            if (im.naturalWidth > 0 && im.naturalHeight > 0) {
              const r = im.naturalWidth / im.naturalHeight;
              setRatio(r);
              onRatio?.(r);
            }
          }}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      ) : subject ? (
        <div className="subj" />
      ) : null}
      {cap ? <span className="cap">{cap}</span> : null}
      {bbox ? (
        <div
          className="bb"
          style={{
            top: `${bbox.top}%`,
            left: `${bbox.left}%`,
            width: `${bbox.width}%`,
            height: `${bbox.height}%`,
          }}
        >
          {bbox.label ? <span className="bb-l">{bbox.label}</span> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}
